import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { MatRadioGroup, MatRadioModule } from '@angular/material/radio';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { DsRadioGroupSize } from '@design-system/ds-radio-group/types/ds-radio-group-size.type';
import { DsRadioGroupValue } from '@design-system/ds-radio-group/types/ds-radio-group-value.type';
import { DsRadioGroupOption } from '@design-system/ds-radio-group/interfaces/ds-radio-group-option.interface';
import { DsRadioGroupChange } from '@design-system/ds-radio-group/interfaces/ds-radio-group-change.interface';

@Component({
  selector: 'ds-radio-group',
  templateUrl: './ds-radio-group.component.html',
  styleUrls: ['./ds-radio-group.component.scss'],
  standalone: true,
  imports: [MatRadioModule, NgClass, NgFor, NgIf],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DsRadioGroupComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DsRadioGroupComponent implements ControlValueAccessor {
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  /**
   * Controls the size variant for every option.
   *
   * @default 'sm'
   */
  @Input() size: DsRadioGroupSize = 'sm';

  /**
   * Collection of radio options to render.
   */
  @Input() options: DsRadioGroupOption[] = [];

  /**
   * Emits when selected value changes.
   */
  @Output() change = new EventEmitter<DsRadioGroupChange>();

  /** Internal disabled state controlled by Angular forms. */
  isDisabled = false;

  /** Internal selected value controlled by Angular forms. */
  value: DsRadioGroupValue = null;

  /** ControlValueAccessor callback for value updates. */
  private onChange: (value: DsRadioGroupValue) => void = (): void => {};

  /** ControlValueAccessor callback for touch updates. */
  private onTouched: () => void = (): void => {};

  /**
   * Stable `trackBy` strategy keyed by option value.
   *
   * @param _index Zero-based index provided by Angular
   * @param option Radio option object
   * @returns Stable option key for ngFor diffing
   */
  trackByRadioGroupOptionValue(_index: number, option: DsRadioGroupOption): string | number | boolean | null {
    return option.value;
  }

  /**
   * Writes a new selected value from Angular forms API.
   *
   * @param value New selected radio value
   */
  writeValue(value: DsRadioGroupValue): void {
    this.value = value;
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Registers callback invoked when value changes.
   *
   * @param fn Callback provided by Angular forms API
   */
  registerOnChange(fn: (value: DsRadioGroupValue) => void): void {
    this.onChange = fn;
  }

  /**
   * Registers callback invoked when control is touched.
   *
   * @param fn Callback provided by Angular forms API
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * Updates disabled state from Angular forms API.
   *
   * @param isDisabled Whether control must be disabled
   */
  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Selects an option when a user activates the whole option container.
   *
   * @param option Option to select
   */
  onOptionSelect(option: DsRadioGroupOption): void {
    if (this.isDisabled || !!option.disabled || this.value === option.value) {
      return;
    }

    this.value = option.value;
    this.changeDetectorRef.markForCheck();
    this.onChange(this.value);
    this.onTouched();
    this.change.emit({ value: this.value });
  }

  /**
   * Synchronizes value updates from native radio-group changes.
   *
   * @param value - changed radio value
   */
  onGroupChange(value: DsRadioGroupValue): void {
    if (this.isDisabled || this.value === value) {
      return;
    }

    this.value = value;
    this.changeDetectorRef.markForCheck();
    this.onChange(this.value);
    this.onTouched();
    this.change.emit({ value: this.value });
  }
}
